import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { useAppDispatch } from '@/Redux/store';
import { useBranches } from '@/Pages/Operations/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import PanelResult from './PanelResult';
import SecretRevealDialog from './SecretRevealDialog';
import WebhookEndpointDetail from './WebhookEndpointDetail';
import {
  WEBHOOKS_PERMISSIONS,
  WEBHOOKS_ROUTES,
  invalidateWebhookEndpoint,
  useWebhookEndpointsQuery,
  useWebhookEventTypesQuery,
  type CreateWebhookEndpointBody,
  type WebhookEndpointWithSecret,
} from '../webhooksApi';
import { apiMessage, formatDateTime, toneForWebhookEndpoint } from '../lib';

const STATUSES = ['ACTIVE', 'DISABLED', 'DISABLED_FAILING'] as const;

/**
 * Screen 3 — this venue's outbound webhook endpoints. `DISABLED_FAILING` is
 * its own status, not a variant reading of `DISABLED`, precisely so this list
 * can say "the platform turned this off" and "you turned this off" apart
 * without anyone reading a log — `WebhooksService.disable` writes a fixed
 * sentence for the manual case, `disabledReason` from `delivery-policy.ts`
 * writes a specific one naming the host, the count and the last error for the
 * automatic one.
 */
export default function WebhooksPanel() {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { can } = usePermission();
  const canManage = can(WEBHOOKS_PERMISSIONS.manage);

  const [status, setStatus] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [issuedSecret, setIssuedSecret] = useState<WebhookEndpointWithSecret | null>(null);

  const query = {
    limit: 100,
    ...(status ? { status: status as (typeof STATUSES)[number] } : {}),
  };
  const { data, isFetching, isError, error, refetch } = useWebhookEndpointsQuery(query);

  const filtered = Boolean(status);
  const clearFilters = () => setStatus('');

  const onCreated = (issued: WebhookEndpointWithSecret) => {
    dispatch(invalidateWebhookEndpoint());
    setCreating(false);
    setIssuedSecret(issued);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <Field label={t('integrations.webhooks.filterStatus')} controlId="wh-status">
            <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-52">
              <option value="">{t('integrations.webhooks.filterAllStatuses')}</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`integrations.webhookEndpointStatus.${s}`)}
                </option>
              ))}
            </Select>
          </Field>
          {filtered && (
            <Button variant="outline" onClick={clearFilters}>
              {t('integrations.states.clearFilters')}
            </Button>
          )}
        </div>
        {canManage && (
          <Button onClick={() => setCreating(true)}>
            <Icon as={Plus} />
            {t('integrations.webhooks.newEndpoint')}
          </Button>
        )}
      </div>

      <PanelResult
        loading={isFetching}
        error={isError ? apiMessage(error, t('integrations.states.loadFailed')) : null}
        count={data?.items.length ?? 0}
        onRetry={refetch}
        filtered={filtered}
        onClearFilters={clearFilters}
        emptyTitle={t('integrations.webhooks.emptyTitle')}
        emptyHint={t('integrations.webhooks.emptyHint')}
        emptyAction={
          canManage && (
            <Button size="sm" onClick={() => setCreating(true)}>
              <Icon as={Plus} />
              {t('integrations.webhooks.newEndpoint')}
            </Button>
          )
        }
      >
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('integrations.webhooks.name')}</TH>
                <TH>{t('integrations.webhooks.url')}</TH>
                <TH>{t('integrations.webhooks.eventTypes')}</TH>
                <TH>{t('integrations.webhooks.status')}</TH>
                <TH>{t('integrations.installations.lastFailure')}</TH>
              </TR>
            </THead>
            <tbody>
              {(data?.items ?? []).map((endpoint) => (
                <TR key={endpoint.id} className="cursor-pointer" onClick={() => setOpenId(endpoint.id)}>
                  <TD className="font-medium">{endpoint.name}</TD>
                  <TD className="max-w-[16rem] truncate text-sm text-ink-muted" title={endpoint.url}>
                    {endpoint.url}
                  </TD>
                  <TD className="text-sm text-ink-muted">{endpoint.eventTypes.length}</TD>
                  <TD>
                    <Badge tone={toneForWebhookEndpoint(endpoint.status)}>
                      {t(`integrations.webhookEndpointStatus.${endpoint.status}`)}
                    </Badge>
                  </TD>
                  <TD className="text-sm tabular-nums text-ink-muted">
                    {formatDateTime(endpoint.lastFailureAt, i18n.language)}
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </PanelResult>

      {creating && (
        <CreateEndpointDialog onClose={() => setCreating(false)} onCreated={onCreated} />
      )}

      <SecretRevealDialog
        open={issuedSecret !== null}
        secret={issuedSecret?.secret ?? null}
        title={t('integrations.webhooks.secretTitle')}
        intro={t('integrations.webhooks.secretIntro')}
        onClose={() => setIssuedSecret(null)}
      />

      {openId && <WebhookEndpointDetail id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function CreateEndpointDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (issued: WebhookEndpointWithSecret) => void;
}) {
  const { t } = useTranslation();
  const branches = useBranches();
  const eventTypes = useWebhookEventTypesQuery();

  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [branchId, setBranchId] = useState('');
  const [types, setTypes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (type: string) =>
    setTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));

  const submit = async () => {
    if (!name.trim() || !url.trim() || types.length === 0) {
      setError(t('integrations.webhooks.createValidation'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body: CreateWebhookEndpointBody = {
        name: name.trim(),
        url: url.trim(),
        eventTypes: types,
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(branchId ? { branchId } : {}),
      };
      const { data } = await api.post<WebhookEndpointWithSecret>(WEBHOOKS_ROUTES.endpoints, body);
      toast.success(t('integrations.webhooks.created'));
      onCreated(data);
    } catch (requestError) {
      setError(apiMessage(requestError, t('integrations.errors.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('integrations.webhooks.newEndpoint')}
      className="max-w-lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={busy} onClick={() => void submit()}>
            {t('integrations.webhooks.create')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label={t('integrations.webhooks.name')} required>
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
        </Field>
        <Field
          label={t('integrations.webhooks.url')}
          required
          hint={t('integrations.webhooks.urlHint')}
        >
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://hooks.example.com/tarabeeza"
            maxLength={2048}
          />
        </Field>
        <Field label={t('integrations.webhooks.description')}>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} />
        </Field>
        {branches.length > 0 && (
          <Field label={t('integrations.common.branch')} hint={t('integrations.webhooks.branchHint')}>
            <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              <option value="">{t('integrations.providers.wholeVenue')}</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label={t('integrations.webhooks.eventTypes')} required>
          <div className="max-h-52 space-y-1.5 overflow-y-auto rounded-lg border p-2">
            {(eventTypes.data ?? []).map((def) => (
              <label key={def.type} className="flex items-start gap-2 rounded p-1 text-sm hover:bg-hover">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={types.includes(def.type)}
                  onChange={() => toggle(def.type)}
                />
                <span>
                  <span className="font-mono">{def.type}</span>
                  <span className="block text-xs text-ink-muted">{def.description}</span>
                </span>
              </label>
            ))}
          </div>
        </Field>
        {error && (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}
